import {
  FreshServiceProposal,
  SelectedProposalPackage,
  ServiceProposalPackage,
  SubmitFreshServiceProposal,
} from "./fresh-service-proposal"
import { isWashInPackages } from "./proposal-wash-utils"
import { ServiceAction } from "./service-part-action"

/**
 * Product choices start undecided on purpose. Pre-selecting the mechanic's
 * recommendation would put words in the customer's mouth: the payload would
 * claim a choice they never made. They pick in "Dina produktval" before
 * approving.
 */
const noProductDecisions = (): Record<string, string> => ({})

export const createSelectedPackage = (
  servicePackage: ServiceProposalPackage,
): SelectedProposalPackage => ({
  originalPackageId: servicePackage.originalPackageId,
  selectedPartActions: servicePackage.partActions.map((action) => ({
    partId: action.partId,
    productDecisions: noProductDecisions(),
  })),
  ...(servicePackage.wash
    ? { selectedWashOriginalId: servicePackage.wash.originalWashId }
    : {}),
})

/**
 * `packages` is the selected set, not every package on the proposal: a wash that
 * only a dropped package includes is not being done unless it is selected on its
 * own.
 *
 * A group of *two or more* alternatives is a question, and nothing is picked for
 * the customer — exactly as with a product choice. A group of one is not a
 * question, and it is the common shape: the CRM writes a group whenever the
 * mechanic adds any wash. That one, like a lone `wash`, is an ordinary opt-out
 * item and starts selected.
 */
export const getDefaultWashSelection = (
  proposal: FreshServiceProposal,
  packages: ServiceProposalPackage[],
): string | undefined => {
  const alternatives = proposal.washAlternatives?.alternatives
  if (alternatives && alternatives.length > 0) {
    if (alternatives.length > 1) return undefined

    const only = alternatives[0]
    if (!only) return undefined
    return isWashInPackages(only.originalWashId, packages)
      ? undefined
      : only.originalWashId
  }

  if (proposal.wash) {
    return isWashInPackages(proposal.wash.originalWashId, packages)
      ? undefined
      : proposal.wash.originalWashId
  }

  return undefined
}

/**
 * Everything is selected up front: the proposal is what the mechanic
 * recommends, and the customer's job is to opt out of the parts they don't
 * want. Part actions already covered by a package are left out so the work is
 * never charged twice.
 *
 * Packages the optimizer dropped are the one exception. They stay on the
 * proposal so the customer can see them and put them back, but selecting one
 * would charge for it *and* for the package that replaced it.
 */
export const buildDefaultSelection = (
  proposal: FreshServiceProposal,
): SubmitFreshServiceProposal => {
  const packages = proposal.packages.filter(
    (servicePackage) => !servicePackage.isDroppedByOptimizer,
  )

  return {
    selectedPackages: packages.map(createSelectedPackage),
    selectedPartActions: proposal.partActions
      .filter((action) => {
        if (action.action === ServiceAction.DO_NOTHING) return false
        // Only the packages that are actually selected cover their work. Work a
        // dropped package covered and the package replacing it does not is done
        // as a standalone action, the same way removing a package by hand moves
        // it out.
        return !packages.some((servicePackage) =>
          servicePackage.partActions.some(
            (included) => included.partId === action.partId,
          ),
        )
      })
      .map((action) => ({
        partId: action.partId,
        productDecisions: noProductDecisions(),
      })),
    selectedManualActions: proposal.manualActions.map((action) => ({
      id: action.id,
      productDecisions: noProductDecisions(),
    })),
    selectedWashOriginalId: getDefaultWashSelection(proposal, packages),
    isWashDeselected: false,
    // Upsells are opt-in: the customer adds what they want in "Tillval".
    selectedUpsellProductIds: [],
    hasCompletedServiceStep: false,
  }
}
